import React from 'react';
import { 
  Box, 
  Typography, 
  Paper,
  Slider,
  Grid,
  Chip,
  Divider
} from '@mui/material';
import { 
  Settings as SettingsIcon,
  People as PeopleIcon,
  Scale as ScaleIcon,
  LocalShipping as LuggageIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const VehicleDetailsSection = ({
  selectedVehicle,
  numberOfPassengers = 2,
  setNumberOfPassengers,
  avgPassengerWeight = 75,
  setAvgPassengerWeight,
  luggageWeight = 50,
  setLuggageWeight,
  showDetails = false
}) => {
  // Calculate total passenger weight
  const totalPassengerWeight = numberOfPassengers * avgPassengerWeight;
  
  // Calculate total vehicle weight
  const baseVehicleWeight = selectedVehicle?.weight || 1500;
  const totalWeight = baseVehicleWeight + totalPassengerWeight + luggageWeight;

  // Calculate adjusted consumption based on weight
  const baseConsumption = selectedVehicle?.consumption || 0.15;
  const weightFactor = totalWeight / baseVehicleWeight;
  const adjustedConsumption = baseConsumption * weightFactor;

  // Calculate adjusted range
  const baseRange = selectedVehicle?.range || 400;
  const adjustedRange = Math.round(baseRange / weightFactor);

  if (!showDetails) return null;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Paper 
        elevation={0} 
        sx={{ 
          p: 2.5, 
          borderRadius: 2, 
          backgroundColor: '#f8f9fa', 
          border: '1px solid #e0e0e0',
          mt: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SettingsIcon sx={{ color: '#4caf50', fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#333' }}>
            Vehicle Configuration
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Number of Passengers */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PeopleIcon sx={{ fontSize: 16, color: '#666' }} />
                  <Typography variant="body2" fontWeight={500}>
                    Passengers
                  </Typography>
                </Box>
                <Chip
                  label={numberOfPassengers}
                  size="small"
                  sx={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    minWidth: 32,
                    height: 24,
                    fontSize: '0.75rem'
                  }}
                />
              </Box>
              <Slider
                value={numberOfPassengers}
                onChange={(e, value) => setNumberOfPassengers(value)}
                min={1}
                max={8}
                step={1}
                sx={{
                  color: '#4caf50',
                  '& .MuiSlider-thumb': {
                    width: 20,
                    height: 20,
                    backgroundColor: '#4caf50',
                    border: '2px solid white',
                    boxShadow: '0 2px 6px rgba(76, 175, 80, 0.3)',
                    '&:hover': {
                      boxShadow: '0 2px 6px rgba(76, 175, 80, 0.5)',
                    }
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: '#4caf50',
                    border: 'none',
                    height: 6
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: '#e0e0e0',
                    height: 6
                  }
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">1</Typography>
                <Typography variant="caption" color="text.secondary">8</Typography>
              </Box>
            </Box>
          </Grid>

          {/* Average Passenger Weight */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ScaleIcon sx={{ fontSize: 16, color: '#666' }} />
                  <Typography variant="body2" fontWeight={500}>
                    Avg Weight
                  </Typography>
                </Box>
                <Chip
                  label={`${avgPassengerWeight}kg`}
                  size="small"
                  sx={{
                    backgroundColor: '#ff9800',
                    color: 'white',
                    minWidth: 50,
                    height: 24,
                    fontSize: '0.75rem'
                  }}
                />
              </Box>
              <Slider
                value={avgPassengerWeight}
                onChange={(e, value) => setAvgPassengerWeight(value)}
                min={40}
                max={120}
                step={5}
                sx={{
                  color: '#ff9800',
                  '& .MuiSlider-thumb': {
                    width: 20,
                    height: 20,
                    backgroundColor: '#ff9800',
                    border: '2px solid white',
                    boxShadow: '0 2px 6px rgba(255, 152, 0, 0.3)',
                    '&:hover': {
                      boxShadow: '0 2px 6px rgba(255, 152, 0, 0.5)',
                    }
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: '#ff9800',
                    border: 'none',
                    height: 6
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: '#e0e0e0',
                    height: 6
                  }
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">40kg</Typography>
                <Typography variant="caption" color="text.secondary">120kg</Typography>
              </Box>
            </Box>
          </Grid>

          {/* Luggage Weight */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LuggageIcon sx={{ fontSize: 16, color: '#666' }} />
                  <Typography variant="body2" fontWeight={500}>
                    Luggage Weight
                  </Typography>
                </Box>
                <Chip
                  label={`${luggageWeight}kg`}
                  size="small"
                  sx={{
                    backgroundColor: '#9c27b0',
                    color: 'white',
                    minWidth: 50,
                    height: 24,
                    fontSize: '0.75rem'
                  }}
                />
              </Box>
              <Slider
                value={luggageWeight}
                onChange={(e, value) => setLuggageWeight(value)}
                min={0}
                max={200}
                step={10}
                sx={{
                  color: '#9c27b0',
                  '& .MuiSlider-thumb': {
                    width: 20,
                    height: 20,
                    backgroundColor: '#9c27b0',
                    border: '2px solid white',
                    boxShadow: '0 2px 6px rgba(156, 39, 176, 0.3)',
                    '&:hover': {
                      boxShadow: '0 2px 6px rgba(156, 39, 176, 0.5)',
                    }
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: '#9c27b0',
                    border: 'none',
                    height: 6
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: '#e0e0e0',
                    height: 6
                  }
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">0kg</Typography>
                <Typography variant="caption" color="text.secondary">200kg</Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Weight Summary */}
        <Box sx={{ 
          p: 2, 
          backgroundColor: 'white', 
          borderRadius: 1.5,
          border: '1px solid #e8f5e8'
        }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, color: '#333' }}>
            Weight & Performance Summary
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Vehicle Weight
                </Typography>
                <Typography variant="body2" fontWeight={600} color="#666">
                  {baseVehicleWeight}kg
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Passenger Weight
                </Typography>
                <Typography variant="body2" fontWeight={600} color="#4caf50">
                  {totalPassengerWeight}kg
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Luggage Weight
                </Typography>
                <Typography variant="body2" fontWeight={600} color="#9c27b0">
                  {luggageWeight}kg
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Total Weight
                </Typography>
                <Typography variant="body2" fontWeight={600} color="#333">
                  {totalWeight}kg
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Consumption
                </Typography>
                <Typography variant="body2" fontWeight={600} color="#ff9800">
                  {adjustedConsumption.toFixed(3)} kWh/km
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Estimated Range
                </Typography>
                <Typography variant="body2" fontWeight={600} color="#2196f3">
                  {adjustedRange}km
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </motion.div>
  );
};

export default VehicleDetailsSection;